/* eslint-disable react/jsx-props-no-spreading */
import PropTypes from 'prop-types';
import clsx from 'clsx';
import React, {
  memo, useState, useEffect, useCallback,
} from 'react';
import { useDropzone } from 'react-dropzone';
import './file-upload.scss';
import { FileUploadTypes, PFX_MIME_TYPE } from './constants';
import MiniFileUpload from './views/mini-file-upload/mini-file-upload';
import DefaultFileUpload from './views/default-file-upload/default-file-upload';
import UploadedFileSection from './views/uploaded-file-section/uploaded-file-section';
import CardFileUpload from './views/card-file-upload/card-file-upload';

const FileUpload = ({
  type, multiple, disabled, accept, maxSize, onFileDrop, onFileDelete, showUploadedSection,
  children, resetFiles, uploadedFileList, primaryMsg, buttonProps, supportingMsg, fetchConfig,
  onFileUploadSuccess, autoUpload, onFileUploadFailure, showAPIError, showFileTypeIcon,
  allowFileNameEditing, ...props
}) => {
  const [errorMessages, setErrorMessages] = useState([]);
  const [isSelectDialogOpen, setIsSelectDialogOpen] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState(uploadedFileList);
  const allFilesUploaded = !multiple && uploadedFiles.length > 0;
  const hideBorder = allFilesUploaded || type === FileUploadTypes.CUSTOM
    || type === FileUploadTypes.MINI;

  useEffect(() => {
    if (uploadedFileList.length > 0) {
      setUploadedFiles(uploadedFileList);
    }
  }, [uploadedFileList]);

  useEffect(() => {
    if (type === FileUploadTypes.MINI && Array.isArray(props.files)) {
      setUploadedFiles(props.files);
    }
  }, [props.files]);

  useEffect(() => {
    if (resetFiles) {
      setUploadedFiles([]);
    }
  }, [resetFiles]);

  const onDrop = useCallback((acceptedFiles, rejectedFiles) => {
    if (allFilesUploaded && errorMessages?.length > 0) {
      setErrorMessages([]);
    }

    if (accept.indexOf(PFX_MIME_TYPE) > -1) {
      const invalidRejectedFiles = [];
      rejectedFiles.forEach(({ file }) => {
        const fileTokens = file.name.split('.');
        const fileFormat = fileTokens[fileTokens.length - 1];
        if (fileFormat === 'pfx') {
          invalidRejectedFiles.push(file);
        }
      });
      acceptedFiles.push(...invalidRejectedFiles);
    }
    const mappedAcc = acceptedFiles.map((file) => ({ file, errors: [] }));
    const allFiles = multiple ? [...uploadedFiles, ...mappedAcc] : mappedAcc;
    if (type === FileUploadTypes.MINI) {
      props?.setFiles(allFiles);
    } else {
      setUploadedFiles(allFiles);
      onFileDrop(acceptedFiles);
    }
  }, [allFilesUploaded, errorMessages]);

  const onFileDialogCancel = useCallback(() => {
    setIsSelectDialogOpen(false);
  }, []);

  const onFileDialogOpen = useCallback(() => {
    setIsSelectDialogOpen(true);
  }, []);

  const onDelete = useCallback((file) => {
    const allFiles = uploadedFiles.filter((fw) => fw.file !== file);
    if (type === FileUploadTypes.MINI) {
      props.setFiles(allFiles);
    } else {
      setUploadedFiles(allFiles);
      onFileDelete(file);
    }
  }, [uploadedFiles]);

  const updateFileName = useCallback((fileIndex, name) => {
    setUploadedFiles(uploadedFiles.map((file, index) => {
      if (index === fileIndex) {
        return {
          file: new File([file.file], name, { type: file.file.type }),
          errors: file.errors,
        };
      }
      return file;
    }));
  }, [uploadedFiles]);

  const { rootRef, getRootProps, getInputProps } = useDropzone({
    onDrop,
    multiple,
    disabled,
    accept,
    maxSize,
    onFileDialogOpen,
    onFileDialogCancel,
  });

  const openFileUploadWindow = useCallback(() => {
    if (rootRef.current) {
      rootRef.current.click();
    }
  }, []);

  const getBodyView = () => {
    switch (type) {
      case FileUploadTypes.MINI:
        return uploadedFiles.length === 0 && (
          <MiniFileUpload
            disabled={disabled}
            primaryMsg={primaryMsg}
            buttonProps={buttonProps}
          />
        );

      case FileUploadTypes.CUSTOM:
        return children;

      case FileUploadTypes.CARD:
        return (
          <CardFileUpload
            disabled={disabled}
            primaryMsg={primaryMsg}
            supportingMsg={supportingMsg}
            isDialogOpen={isSelectDialogOpen}
            allFilesUploaded={allFilesUploaded}
          />
        );

      case FileUploadTypes.DEFAULT:
        return !allFilesUploaded && (
          <DefaultFileUpload
            disabled={disabled}
            primaryMsg={primaryMsg}
            supportingMsg={supportingMsg}
          />
        );

      default:
        return null;
    }
  };

  const getFileSectionView = () => (
    showUploadedSection && uploadedFiles.map((fileWrapper, index) => (
      <div key={fileWrapper.lastModified} className="uploaded-section">
        <UploadedFileSection
          type={type}
          maxSize={maxSize}
          fileIndex={index}
          onDelete={onDelete}
          file={fileWrapper.file}
          autoUpload={autoUpload}
          fetchConfig={fetchConfig}
          apiErrors={errorMessages}
          fileErrors={fileWrapper.errors}
          updateFileName={updateFileName}
          isDialogOpen={isSelectDialogOpen}
          showFileTypeIcon={showFileTypeIcon}
          allFilesUploaded={allFilesUploaded}
          setErrorMessages={setErrorMessages}
          onFileUploadSuccess={onFileUploadSuccess}
          onFileUploadFailure={onFileUploadFailure}
          openFileUploadWindow={openFileUploadWindow}
          allowFileNameEditing={allowFileNameEditing}
          showAPIError={showAPIError || fileWrapper.errors.length > 0}
        />
      </div>
    ))
  );

  return (
    <div className="file-upload-cont">
      <div
        className={clsx('fuc-upload-btn', { 'hide-border': hideBorder, disabled })}
        {...getRootProps()}
      >
        <input {...getInputProps()} />
        {getBodyView()}
      </div>
      {getFileSectionView()}
    </div>
  );
};

FileUpload.defaultProps = {
  ...UploadedFileSection.defaultProps,
  accept: null,
  maxSize: null,
  children: null,
  disabled: false,
  multiple: false,
  resetFiles: false,
  setFiles: () => {},
  showAPIError: true,
  onFileDrop: () => {},
  uploadedFileList: [],
  onFileDelete: () => {},
  showUploadedSection: true,
  allowFileNameEditing: true,
  type: FileUploadTypes.DEFAULT,
};

FileUpload.propTypes = {
  ...UploadedFileSection.propTypes,
  multiple: PropTypes.bool,
  disabled: PropTypes.bool,
  setFiles: PropTypes.func,
  maxSize: PropTypes.number,
  resetFiles: PropTypes.bool,
  onFileDrop: PropTypes.func,
  onFileDelete: PropTypes.func,
  showAPIError: PropTypes.bool,
  showUploadedSection: PropTypes.bool,
  allowFileNameEditing: PropTypes.bool,
  accept: PropTypes.arrayOf(PropTypes.string),
  type: PropTypes.oneOf(Object.values(FileUploadTypes)),
  uploadedFileList: PropTypes.arrayOf(PropTypes.shape({})),
  children: PropTypes.oneOfType([PropTypes.oneOf([null]), PropTypes.element]),
};

export default memo(FileUpload);
